import { useCallback, useEffect, useState } from "react";
import cls from "classnames";
import toast from "react-hot-toast";
import { MultiCurrency } from "../../../common/utils/currency";
import { QOOHOO_WEBSITE_DOMAIN } from "constants/env";
import useAppState from "store/appState";
import useCouponState from "store/couponState";
import useEntityStore from "store/entityStore";
import httpf from "utils/api/frontend/httpf";
import Spinner from "../../../common/library/loader/Spinner";
import EntityModel from "../models/EntityModel";

const getFeeBreakup = async (entity, order) => {
  const payload = {};

  if (order.isCouponApplied) {
    payload.coupon_code = order.coupon.data.code;
  }

  if (entity.isOneToOneMeeting) {
    payload.one_to_one_meeting_uid = entity.data.uid;
  } else if (entity.isPremiumContent) {
    payload.premium_content_uid = entity.data.uid;
  } else if(entity.isRegularChannel || entity.isCourse) {
    payload.channel_uid = entity.data.uid;
    
    if(!order.isOneTime) {
      payload.plan_uid = order.selectedPlan.data.uid;
    }
  }

  const response = await httpf.post(
    `${QOOHOO_WEBSITE_DOMAIN}/api/order-summary`,
    payload
  );

  const { data } = response?.data || null;

  return data;
};

const FeeBreakupRow = (props) => {
  const {
    className,
    showBottomBorder = false,
    showTopBorder = false,
    label,
    labelClass,
    value,
    valueClass,
  } = props;

  return (
    <div
      className={cls(
        "flex w-full items-center justify-between py-3",
        showBottomBorder && "border-b border-[#D5D8DC] border-dashed",
        showTopBorder && "border-t border-[#D5D8DC] border-dashed",
        className
      )}
    >
      <span className={cls("text-sm text-[#16191B] font-normal", labelClass)}>
        {label}
      </span>
      <span
        className={cls("text-sm text-[#16191B] font-semibold", valueClass)}
      >
       {value}
      </span>
    </div>
  );
};

export default function FeeBreakup(props) {
  const entity = useEntityStore((state) => state.entity);
  const couponDUMMY = useCouponState(state => state.coupon);
  const order = useAppState(state => state.order);

  const [
    isLoadingFeeBreakup, setIsLoadingFeeBreakup, feeBreakup, setFeeBreakup
  ] = useAppState(state => [
    state.isLoadingFeeBreakup, state.setIsLoadingFeeBreakup, state.feeBreakup, state.setFeeBreakup
  ]);

  const shouldShowDiscount = !!feeBreakup?.discount_amount;
  const shouldShowTax = !feeBreakup?.tax_details?.is_tax_inclusive;

  useEffect(() => {
    (async () => {
      try {
        setIsLoadingFeeBreakup(true);
        const data = await getFeeBreakup(entity, order);
        setFeeBreakup(data);
      } catch (err) {
        console.log(err);
        toast.error(
          "Some network error! Order summary cannot be fetched this time."
        );
      } finally {
        setIsLoadingFeeBreakup(false);
      }
    })();
  }, [entity, order.coupon, order.selectedPlan]);

  let content;
  if (isLoadingFeeBreakup) {
    content = (
      <div>
        <Spinner color="rgba(0,0,0,0.2)" className="!h-6 !w-6" />
      </div>
    );
  } else if (feeBreakup) {
    content = (
      <>
        <FeeBreakupRow
          label="Base price"
          value={MultiCurrency.formatAmountWithSymbol(feeBreakup.subtotal_amount, feeBreakup.currency)}
          showBottomBorder
        />
        {shouldShowDiscount && (
          <FeeBreakupRow
            labelClass="!text-[#75AE6B] !font-bold"
            valueClass="!text-[#75AE6B] !font-bold"
            label={`Discount (${order.coupon?.data.code})`}
            value={`- ${MultiCurrency.formatAmountWithSymbol(feeBreakup.discount_amount, feeBreakup.currency)}`}
          />
        )}

        {shouldShowTax && (
          <FeeBreakupRow
            label={`Tax (${feeBreakup.tax_details.tax_percentage}% ${feeBreakup.tax_details.tax_label || feeBreakup.tax_details.tax_type})`}
            value={MultiCurrency.formatAmountWithSymbol(feeBreakup.tax_details.tax_amount, feeBreakup.currency)}
          />
        )}

        {
          feeBreakup.convenience_fee ? (
            <FeeBreakupRow
              label="Convenience fee"
              value={MultiCurrency.formatAmountWithSymbol(feeBreakup.convenience_fee, feeBreakup.currency)}
            />
          ) : null
        }

        <FeeBreakupRow
          label="Total Price"
          labelClass="!text-base !font-bold leading-8"
          valueClass="!text-base !font-bold leading-8"
          value={MultiCurrency.formatAmountWithSymbol(feeBreakup.total_amount, feeBreakup.currency)}
          showTopBorder
        />
      </>
    );
  }

  return (
    <div
      id="feebreakup-section"
      className="py-3 px-4 flex flex-col w-full rounded-lg gap-4 h-fit bg-[#F9FAFA] border border-[#ededed]"
    >
      <span className="font-bold text-[#414141] text-base">Fee breakup</span>
      <div>{content}</div>
    </div>
  );
}
